import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  CollapsedLeftLogoIcon,
  Label,
  LeftLogoIcon,
  Link,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';

import { AuthContext, ClubSelect } from 'auth';
import { CashboxContext } from 'cashbox';
import { ClientSuggest } from 'components';
import { HOST_GROUPS_LOADED_EVENT } from 'consts';
import { FeatureHOC } from 'features';
import { usePageTitles, useWindowResize } from 'hooks';
import { LINKS } from 'links';
import { LocalizationContext } from 'localization';
import {
  ClientModal,
  DepositModal,
  NotPermissionModal,
  ResetPasswordModal,
} from 'modals';
import { isSandbox } from 'sandbox';
import { emitter } from 'services/EventEmitter';
import { SettingsContext } from 'settings';
import { ShellToggleButton } from 'shell';
import { IUser } from 'types';

import { Calls } from './Calls';
import ClientSearchForMobile from './ClientSearchForMobile';
import { FastAccess } from './FastAccess';
import s from './Header.module.scss';
import { NOW_QUERY } from './schema';
import { UserMenu } from './UserMenu';

export const Header: FC = () => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { permissions, isActiveOperator },
  } = useContext(AuthContext);

  const {
    state: {
      settings: { callAdmin, countryCode },
    },
  } = useContext(SettingsContext);

  const [timeDifference, setTimeDifference] = useState<number | null>(null);

  const { data } = useQuery(NOW_QUERY, {
    fetchPolicy: 'network-only',
    pollInterval: 300000,
  });

  const pageTitle = usePageTitles();

  useEffect(() => {
    if (!data) return;

    const serverTime = dayjs(data.now);
    const localTime = dayjs();
    const difference = Math.abs(serverTime.diff(localTime, 'minute'));
    setTimeDifference(difference);
  }, [data]);

  const [isCloudLagging, setIsCloudLagging] = useState(false);

  useEffect(() => {
    const checkRequestTime = (seconds: number): void => {
      setIsCloudLagging(seconds > 5);
    };

    emitter.on(HOST_GROUPS_LOADED_EVENT, checkRequestTime);

    return () => {
      emitter.off(HOST_GROUPS_LOADED_EVENT, checkRequestTime);
    };
  }, []);

  const hideFastAccess = useMemo(
    () =>
      !permissions?.has('payment_create') &&
      !permissions?.has('booking_manage') &&
      !permissions?.has('cash_order_create'),
    [permissions]
  );

  const [clientUUID, setClientUUID] = useState<string | null>(null);
  const [notPermissionModalOpen, setNotPermissionModalOpen] =
    useState<boolean>(false);

  const { OFDState } = useContext(CashboxContext);

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [defaultClient, setIsDefaultClient] = useState<string | undefined>('');

  const [isResetPasswordModal, setIsResetPasswordModal] = useState<
    boolean | undefined
  >(false);
  const [currentClient, setCurrentClient] = useState<IUser | undefined>(
    undefined
  );
  const size = useWindowResize();

  return (
    <header className={s.header}>
      {notPermissionModalOpen && (
        <NotPermissionModal onClose={() => setNotPermissionModalOpen(false)} />
      )}
      {clientUUID && !isDepositOpen && !isResetPasswordModal && (
        <ClientModal
          clientUUID={clientUUID}
          onClose={() => setClientUUID(null)}
          setIsDepositOpen={setIsDepositOpen}
          setIsDefaultClient={setIsDefaultClient}
          setIsResetPasswordModal={setIsResetPasswordModal}
          setCurrentClient={setCurrentClient}
        />
      )}
      {isDepositOpen && (
        <DepositModal
          defaultClientUUID={defaultClient}
          onClose={() => {
            setIsDepositOpen(false);
            setClientUUID(null);
          }}
        />
      )}
      {isResetPasswordModal && (
        <ResetPasswordModal
          currentClient={currentClient}
          onClose={() => setIsResetPasswordModal(false)}
          clientUUID={clientUUID}
        />
      )}
      <RouterLink to="/" className={cn(s.logo)}>
        {size.width < 1865 ? <CollapsedLeftLogoIcon /> : <LeftLogoIcon />}
      </RouterLink>
      {pageTitle && (
        <div className={s.title}>
          {pageTitle}
          {isCloudLagging && (
            <Label
              iconName="CloudAttentionIcon"
              text={t('connection.lag')}
              color="orange"
            />
          )}
          {OFDState.count >= 5 && countryCode === 'RU' && (
            <Tooltip
              contentClassName={s.tooltip}
              header="Ошибка отправки кассовых данных в ОФД. Обязательно сообщите об этом вашему менеджеру"
              content={`Дата последней успешной отправки ${dayjs(
                OFDState.firstErrorDate
              ).format(
                'DD.MM.YYYY, HH:mm:ss'
              )}. С того момента не удалось отправить чеков: ${
                OFDState.count
              } шт.`}
              footer={
                <Link
                  title="Подробнее"
                  link={LINKS['cash_order_tooltip'][lang]}
                />
              }
            >
              <Label color="orange" iconName="InfoIcon" text="Ошибка кассы" />
            </Tooltip>
          )}
          {timeDifference !== null && timeDifference > 1 && (
            <Tooltip
              contentClassName={s.tooltip}
              header={t('header.error.time.title')}
              content={t('header.error.time.text')}
            >
              <Label
                color="orange"
                iconName="InfoIcon"
                text={t('header.error.time.label')}
              />
            </Tooltip>
          )}
        </div>
      )}
      <div className={s.controls}>
        {(permissions?.has('client_browse') ||
          permissions?.has('client_info')) && (
          <ClientSearchForMobile
            setNotPermissionModalOpen={setNotPermissionModalOpen}
            setIsDepositOpen={setIsDepositOpen}
            setIsDefaultClient={setIsDefaultClient}
            setIsResetPasswordModal={setIsResetPasswordModal}
            withReplenishment
            setCurrentClient={setCurrentClient}
          />
        )}
        {isSandbox && <ShellToggleButton />}
        {!hideFastAccess && <FastAccess />}
        {(permissions?.has('client_browse') ||
          permissions?.has('client_info')) && (
          <ClientSuggest
            value={clientUUID}
            onChange={(value) => {
              if (!permissions?.has('client_info')) {
                clientUUID === '' ? setClientUUID(null) : setClientUUID('');
                setNotPermissionModalOpen(true);
                return;
              }

              setClientUUID(value);
            }}
            className={s['client-search']}
            showBorder
            small
            onFocus
            withReplenishment
            setIsDefaultClient={setIsDefaultClient}
            setDepositOpen={setIsDepositOpen}
          />
        )}
        {callAdmin && isActiveOperator && (
          <FeatureHOC alias="call_admin" strategy="hide">
            <Calls />
          </FeatureHOC>
        )}
        <ClubSelect />
        <UserMenu />
      </div>
    </header>
  );
};
